import React, {useMemo} from 'react';
import useInView from '../hooks/useInView';

/*
 * EnsembleDiagram — the "how it works" motif on the homepage. Chronological,
 * left to right: the gate-closure marker stands on the left; forecaster
 * submissions draw in one by one, and finally the ensemble (accent-500, the
 * ensemble color) draws through the middle of the bundle. One-shot on scroll
 * into view: once drawn, every line stays (see `.seq-*` in index.css).
 *
 * The submissions are shaped like a real wind-power spaghetti plot: all
 * members share one large-scale signal (moderate output, a ramp-down, a
 * recovery, then a strong ramp-up towards capacity) but disagree on depth,
 * timing and peak level — tight at issue time, wide at the dip and the peak.
 * Paths are generated from knot lists with a Catmull-Rom spline (see
 * smoothSegments), so every line flows smoothly through its knots.
 *
 * All coordinates are final — the 2.2x vertical stretch is baked into the
 * knot data (y' = 2.2y - 100). No SVG transforms: `vector-effect` breaks
 * `pathLength`-normalized dash-draw in some engines, truncating the lines.
 *
 * The sequence only runs while the figure is in view; reduced-motion users
 * get the static final frame.
 */

const X = [110, 140, 170, 200, 230, 260, 290, 320, 350, 380, 410, 440, 470, 500, 530, 560, 600];

const MEMBER_Y = [
  [243.2, 238.8, 234.4, 243.2, 260.8, 300.4, 335.6, 331.2, 291.6, 225.6, 142, 76, 80.4, 146.4, 124.4, 111.2, 115.6],
  [230, 216.8, 252, 216.8, 300.4, 366.4, 388.4, 300.4, 252, 238.8, 172.8, 120, 120, 115.6, 128.8, 137.6, 93.6],
  [238.8, 221.2, 256.4, 234.4, 265.2, 348.8, 401.6, 348.8, 265.2, 177.2, 120, 124.4, 137.6, 159.6, 98, 102.4, 124.4],
  [225.6, 230, 230, 221.2, 287.2, 304.8, 318, 287.2, 287.2, 168.4, 93.6, 62.8, 89.2, 164, 89.2, 142, 128.8],
  [241, 214.6, 249.8, 216.8, 256.4, 335.6, 340, 344.4, 260.8, 243.2, 155.2, 58.4, 133.2, 102.4, 133.2, 133.2, 89.2],
];

const SIGNAL_Y = [234.4, 225.6, 243.2, 230, 278.4, 331.2, 357.6, 318, 274, 203.6, 133.2, 93.6, 102.4, 133.2, 111.2, 120, 106.8];

const pts = (ys) => X.map((x, i) => [x, ys[i]]);

// Catmull-Rom spline through the knots, emitted as cubic bezier segments
// (uniform): C1 = P1 + (P2 - P0)/6, C2 = P2 - (P3 - P1)/6. Endpoints are
// duplicated so the curve passes through every knot without kinks.
function smoothSegments(points) {
  const p = [points[0], ...points, points[points.length - 1]];
  const f = (n) => Math.round(n * 10) / 10;
  let d = '';
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = p[i];
    const p1 = p[i + 1];
    const p2 = p[i + 2];
    const p3 = p[i + 3];
    const c1x = f(p1[0] + (p2[0] - p0[0]) / 6);
    const c1y = f(p1[1] + (p2[1] - p0[1]) / 6);
    const c2x = f(p2[0] - (p3[0] - p1[0]) / 6);
    const c2y = f(p2[1] - (p3[1] - p1[1]) / 6);
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
  }
  return d;
}

function smoothPath(points) {
  return `M${points[0][0]},${points[0][1]}${smoothSegments(points)}`;
}

const LABEL_STYLE = {textTransform: 'uppercase', letterSpacing: '0.12em'};

export default function EnsembleDiagram() {
  const [ref, inView] = useInView();

  const {submissions, ensemble} = useMemo(() => {
    return {
      submissions: MEMBER_Y.map((ys) => smoothPath(pts(ys))),
      ensemble: smoothPath(pts(SIGNAL_Y)),
    };
  }, []);

  const strokeClass = (cls) => (inView ? cls : 'seq-hidden-stroke');
  const fillClass = (cls) => (inView ? cls : 'seq-hidden');
  const delay = (ms) => ({'--seq-delay': `${ms}ms`});

  return (
    <figure ref={ref} className="w-full">
      <svg
        viewBox="0 0 640 500"
        className="w-full h-auto"
        role="img"
        aria-label="At gate closure, forecaster submissions appear one by one and merge into a single ensemble forecast with a quantile band"
      >
        {/* submissions: draw in one by one, then stay */}
        {submissions.map((d, i) => (
          <path
            key={d}
            d={d}
            pathLength={1}
            fill="none"
            strokeLinecap="round"
            strokeWidth="1.25"
            className={`stroke-slate-400/60 dark:stroke-gray-500/50 ${strokeClass('seq-draw')}`}
            style={delay(i * 400)}
          />
        ))}

        {/* ensemble line */}
        <path
          d={ensemble}
          pathLength={1}
          fill="none"
          strokeLinecap="round"
          strokeWidth="2.5"
          className={`stroke-accent-500 ${strokeClass('seq-draw')}`}
          style={delay(3200)}
        />

        {/* gate-closure marker */}
        <line
          x1="90"
          y1="23.2"
          x2="90"
          y2="445.6"
          strokeDasharray="3 4"
          className="stroke-slate-900/20 dark:stroke-gray-100/20"
        />

        {/* labels */}

        <text
          x="90" y="14" fontSize="10" textAnchor="middle" style={LABEL_STYLE}
          className="fill-slate-400 dark:fill-gray-500"
        >
          gate closure
        </text>

        {/* sequence labels, timed with their element */}
        <text
          x="110" y="168" fontSize="10" style={{...LABEL_STYLE, ...delay(200)}}
          className={`fill-slate-400 dark:fill-gray-500 ${fillClass('seq-fade')}`}
        >
          submissions
        </text>
        <text
          x="600" y="76" fontSize="10" textAnchor="end" style={{...LABEL_STYLE, ...delay(3300)}}
          className={`fill-accent-600 dark:fill-accent-400 ${fillClass('seq-fade')}`}
        >
          ensemble
        </text>
      </svg>
    </figure>
  );
}
